import React, { useMemo, useState } from 'react';
import { FiChevronRight, FiChevronDown, FiFile } from 'react-icons/fi';

export interface PreviewFile {
  path: string;
  size: number;
  kind: string;
  content: string | null;
}

interface TreeNode {
  name: string;
  path: string | null; // set on file leaves
  size: number;
  children: Map<string, TreeNode>;
}

function buildTree(files: PreviewFile[]): TreeNode {
  const root: TreeNode = { name: '', path: null, size: 0, children: new Map() };
  for (const f of files) {
    const segments = f.path.split('/').filter(Boolean);
    let node = root;
    segments.forEach((seg, i) => {
      const isLeaf = i === segments.length - 1;
      let child = node.children.get(seg);
      if (!child) {
        child = { name: seg, path: null, size: 0, children: new Map() };
        node.children.set(seg, child);
      }
      if (isLeaf) {
        child.path = f.path;
        child.size = f.size;
      }
      node = child;
    });
  }
  return root;
}

// Directories first, then files; each alphabetical.
function sortedChildren(node: TreeNode): TreeNode[] {
  return Array.from(node.children.values()).sort((a, b) => {
    const aDir = a.children.size > 0 ? 0 : 1;
    const bDir = b.children.size > 0 ? 0 : 1;
    if (aDir !== bDir) return aDir - bDir;
    return a.name.localeCompare(b.name);
  });
}

interface RowProps {
  node: TreeNode;
  depth: number;
  selected: string | null;
  onSelect: (path: string) => void;
}

const Row: React.FC<RowProps> = ({ node, depth, selected, onSelect }) => {
  const isDir = node.children.size > 0;
  const [open, setOpen] = useState(true); // default-expanded
  const pad = { paddingLeft: `${depth * 14 + 8}px` };

  if (isDir) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={pad}
          className="flex w-full items-center gap-1 py-1 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {open ? <FiChevronDown className="shrink-0" /> : <FiChevronRight className="shrink-0" />}
          <span className="truncate font-medium">{node.name}</span>
        </button>
        {open &&
          sortedChildren(node).map((child) => (
            <Row key={child.name} node={child} depth={depth + 1} selected={selected} onSelect={onSelect} />
          ))}
      </div>
    );
  }

  const isSelected = selected === node.path;
  return (
    <button
      type="button"
      onClick={() => node.path && onSelect(node.path)}
      style={pad}
      className={`flex w-full items-center gap-1 py-1 text-left text-sm ${
        isSelected
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
      }`}
    >
      <FiFile className="shrink-0 opacity-60" />
      <span className="truncate">{node.name}</span>
    </button>
  );
};

interface PreviewFileTreeProps {
  files: PreviewFile[];
  selected: string | null;
  onSelect: (path: string) => void;
}

export const PreviewFileTree: React.FC<PreviewFileTreeProps> = ({ files, selected, onSelect }) => {
  const root = useMemo(() => buildTree(files), [files]);
  const rows = sortedChildren(root);

  if (rows.length === 0) {
    return (
      <div className="p-3 text-sm text-gray-400 dark:text-gray-500">No files.</div>
    );
  }

  return (
    <div className="overflow-auto">
      {rows.map((child) => (
        <Row key={child.name} node={child} depth={0} selected={selected} onSelect={onSelect} />
      ))}
    </div>
  );
};
